import React, {
  useState, useEffect, forwardRef, useImperativeHandle,
} from 'react';
import { useQuery } from 'react-apollo';
import LoadingOverlay from 'react-loading-overlay';
import Paginator from 'react-hooks-paginator';
import PropTypes from 'prop-types';
import { map } from 'lodash';
import {
  Request, Deactivate, ManagerDetail, DeleteUser,
} from '../../modals';
import { staticConstants, loaderStyle, NUMERIC_VALUE } from '../../../utils';
import { GET_ALL_USER_QUERY } from '../../../graphql-client/queries';
import ManagerItem from './ManagerItem';
import EditProfileDetail from './EditProfileDetail';
import RenderTableHeader from './RenderTableHeader';

const ManagerList = forwardRef((props, ref) => {
  const [requestModal, setRequestModal] = useState(false);
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editManagerModal, setEditManagerModal] = useState(false);
  const [managerDetailModal, setManagerDetailModal] = useState(false);
  const [managerEditData, setManagerEditData] = useState(false);
  const [modalObj, setModalObj] = useState({});
  const [modalItemObj, setModalItemObj] = useState({});
  const pageLimit = NUMERIC_VALUE.TWENTY;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKey, setSearchKey] = useState(undefined);
  const [requestModalObj, setRequestModalObj] = useState({});

  const [filter, setTableFilter] = useState({
    sort: '',
    sortBy: 0,
  });
  const [refreshList, setRefreshList] = useState(false);
  const [statusFilter, setStatusFilter] = useState(undefined);

  const toggleRequestModal = (managerDetail) => {
    setRequestModalObj(managerDetail);
    setRequestModal(!requestModal);
  };
  const toggleDeactivateModal = (managerDetail) => {
    setModalObj(managerDetail);
    setDeactivateModal(!deactivateModal);
  };

  const toggleDeleteModal = (managerDetail) => {
    setModalObj(managerDetail);
    setDeleteModal(!deleteModal);
  };

  const toggleEditManagerModal = (managerProfileData) => {
    setManagerEditData(managerProfileData);
    setEditManagerModal(!editManagerModal);
  };
  const toggleManagerDetailModal = (item, img, date) => {
    const updatedItem = { ...item, img, registerDate: date };
    setModalItemObj(updatedItem);
    setManagerDetailModal(!managerDetailModal);
  };

  const queryObj = {
    role: staticConstants.ROLE.MANAGER,
    page: currentPage,
    limit: pageLimit,
    searchKey,
    statusFilter,
    ...filter,
  };

  const classNameObj = {
    flexCol9: 'd-flex-table-col-9',
    flexCol11: 'd-flex-table-col-11',
    flexCol13: 'd-flex-table-col-13',
    flexCol16: 'd-flex-table-col-16',
  };

  useImperativeHandle(ref, () => ({
    searchManager(search, filterValue) {
      setCurrentPage(1);
      setSearchKey(search);
      setStatusFilter(filterValue);
      setTableFilter({
        sort: '',
        sortBy: 0,
      });
    },
  }));

  const refreshUserList = () => {
    setRefreshList(true);
  };

  const { loading, data, refetch: getManagerData } = useQuery(
    GET_ALL_USER_QUERY,
    {
      variables: queryObj,
    },
  );

  useEffect(() => {
    if (data && data.getAllUser) {
      props.setTotalCount(data.getAllUser.count);
    }
  }, [data]);

  useEffect(() => {
    if (currentPage || filter || searchKey || refreshList) {
      getManagerData();
      setRefreshList(false);
    }
  },
  [currentPage, filter, searchKey, refreshList]);

  const tableContent = {
    managerFullName: { name: 'Label', class: `${classNameObj.flexCol13}`, sort: true },
    email: { name: 'Email Address', class: `${classNameObj.flexCol16}`, sort: false },
    contactNumber: { name: 'Phone', class: `${classNameObj.flexCol11}`, sort: false },
    createdAt: { name: 'Registered on', class: `${classNameObj.flexCol11}`, sort: true },
    totalArtist: { name: 'Artist Representing', class: `${classNameObj.flexCol16} col-center`, sort: true },
    companyName: { name: 'Company', class: `${classNameObj.flexCol13}`, sort: true },
    status: { name: 'Status', class: `${classNameObj.flexCol11}`, sort: true },
    totalOffers: { name: 'Gigs', class: `${classNameObj.flexCol9}`, sort: true },
  };

  const sortTableData = (sort, sortBy) => {
    setTableFilter({ sort, sortBy });
  };

  const renderManagerList = () => {
    if (data && data.getAllUser) {
      const dataList = data.getAllUser.data;
      return map(dataList, (item, index) => item && (
      <ManagerItem
        key={index}
        item={item}
        toggleManagerDetailModal={toggleManagerDetailModal}
        toggleRequestModal={toggleRequestModal}
        toggleDeactivateModal={toggleDeactivateModal}
        toggleDeleteModal={toggleDeleteModal}
        toggleEditManagerModal={(managerProfileData) => toggleEditManagerModal(managerProfileData)}
      />
      ));
    }
    return null;
  };
  return (
    <div className="table-wrapper">
      <div className={`d-flex-table ${data && data.getAllUser && data.getAllUser.count === 0 ? 'no-result' : ''}`}>
        <div className="d-flex-table-head">
          <div className="d-flex-table-row">
            <RenderTableHeader
              tableContent={tableContent}
              filter={filter}
              sortData={sortTableData}
            />
          </div>
        </div>
        <div className="d-flex-table-body">
          <LoadingOverlay
            active={loading}
            spinner
            text=""
            className="offerListContainerLoader"
            styles={loaderStyle}
          />
          <div className="d-flex-table-body-wrapper">
            {renderManagerList()}
            {data && data.getAllUser && data.getAllUser.paginationCount === 0 && (
            <div className="no-offer-table-data">
              <div className="no-table-data">
                <h2>No Results found</h2>
                <p>Try using a different name or keyword</p>
              </div>
            </div>
            )}
          </div>
          <Paginator
            totalRecords={data && data.getAllUser ? data.getAllUser.paginationCount : 0}
            pageLimit={pageLimit}
            pageNeighbours={NUMERIC_VALUE.TWO}
            setOffset={() => {}}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageNextClass="paginationNextButton"
            pagePrevClass="paginationPrevButton"
          />
        </div>
      </div>
      {(requestModal && requestModalObj) && (
        <Request
          isOpen={requestModal}
          onClose={() => { toggleRequestModal(); }}
          userDetail={requestModalObj}
          userRole={staticConstants.ROLE.MANAGER}
          updateUserList={() => { refreshUserList(); }}
        />
      )}
      {(deactivateModal && modalObj) && (
        <Deactivate
          isOpen={deactivateModal}
          onClose={() => { toggleDeactivateModal(); }}
          userDetail={modalObj}
          role={staticConstants.ROLE_TYPE.MANAGER}
          updateUserList={() => { refreshUserList(); }}
        />
      )}
      {(deleteModal && modalObj) && (
        <DeleteUser
          isOpen={deleteModal}
          onClose={() => { toggleDeleteModal(); }}
          userDetail={modalObj}
          role={staticConstants.ROLE_TYPE.MANAGER}
          updateUserList={() => { refreshUserList(); }}
        />
      )}
      {editManagerModal && managerEditData && (
        <EditProfileDetail
          isOpen={editManagerModal}
          managerObject={managerEditData}
          userRole={staticConstants.ROLE.MANAGER}
          onClose={() => { toggleEditManagerModal(); }}
          updateUserList={() => { refreshUserList(); }}
        />
      )}
      {managerDetailModal && (
        <ManagerDetail
          isOpen={managerDetailModal}
          onClose={() => { toggleManagerDetailModal(); }}
          item={modalItemObj}
          userId={modalItemObj._id}
          openEditProfileForm={(managerData) => { toggleEditManagerModal(managerData); }}
          updateUserList={() => { refreshUserList(); }}
          toggleDeactivateModal={toggleDeactivateModal}
        />
      )}
    </div>
  );
});

ManagerList.propTypes = {
  setTotalCount: PropTypes.number.isRequired,
};

export default ManagerList;
